/**
 * Turning a phone away, for now (docs/milestones/BUILD-PLAN.md, "mobile").
 *
 * The game is played with a keyboard, a mouse or a gamepad. There are no touch controls, so on a
 * phone or tablet there is literally no way to move your ranger - and the UI is a fixed 640x360
 * canvas that collapses to a postage stamp in portrait. Letting someone load all that and discover
 * it for themselves is the same sin the missions used to commit: the rule was real, and invisible.
 *
 * So it is said plainly, before the renderer is even built, and it says WHY.
 */

/** A device with no mouse to hover and a finger for a pointer: a phone or a tablet. */
export const TOUCH_ONLY = '(hover: none) and (pointer: coarse)';

/** The URL flag that gets past this, for an iPad with a keyboard and for anyone debugging. */
export const OVERRIDE = 'anyway';

/**
 * Should this device be turned away? Asked of a media matcher so it can be tested, and false
 * whenever the answer is not a clear yes - a browser too old to ask is never blocked. Pure.
 */
export function needsDesktop(matches: (query: string) => boolean): boolean {
  try {
    return matches(TOUCH_ONLY);
  } catch {
    // No `matchMedia`, or it threw: never keep a player out over a question we could not ask.
    return false;
  }
}

/** The real matcher, or one that always says no where there is no `matchMedia` (jsdom, an old browser). */
export const browserMatcher = (query: string): boolean =>
  typeof globalThis.matchMedia === 'function' && globalThis.matchMedia(query).matches;

const WRAP = 'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;'
  + 'background:#14121a;color:#ffb000;font:16px/1.6 ui-monospace,Consolas,monospace;'
  + 'padding:24px;box-sizing:border-box;z-index:99';
const BOX = 'max-width:34em;text-align:center';
const TITLE = 'font-size:13px;letter-spacing:3px;opacity:0.7;margin:0 0 18px';
const HEAD = 'font-size:20px;letter-spacing:2px;margin:0 0 20px';
const BODY = 'color:#d8d2c8;font-size:15px;margin:0 0 14px';
const LINK = 'display:inline-block;margin-top:20px;color:#ffb000;background:none;'
  + 'border:1px solid #ffb000;padding:10px 18px;font:inherit;font-size:13px;letter-spacing:2px;'
  + 'cursor:pointer;-webkit-tap-highlight-color:transparent';
const SMALL = 'display:block;margin-top:10px;font-size:12px;color:#d8d2c8;opacity:0.6';

function line(tag: string, style: string, text: string): HTMLElement {
  const el = document.createElement(tag);
  el.setAttribute('style', style);
  el.textContent = text;
  return el;
}

/**
 * Fills the page with the notice and returns true, so the caller can stop before building a
 * renderer this device will not use. `onAnyway` lets the player through regardless.
 */
export function showDesktopOnly(root: HTMLElement, onAnyway: () => void): void {
  const wrap = document.createElement('div');
  wrap.setAttribute('style', WRAP);
  const box = document.createElement('div');
  box.setAttribute('style', BOX);

  const anyway = document.createElement('button');
  anyway.setAttribute('style', LINK);
  anyway.textContent = 'CONTINUE ANYWAY';
  anyway.addEventListener('click', () => {
    wrap.remove();
    onAnyway();
  });

  box.append(
    line('div', TITLE, 'PROJECT NIGHTDROP'),
    line('h1', HEAD, 'THIS ONE NEEDS A KEYBOARD.'),
    line('p', BODY, 'A ranger is flown with a keyboard and mouse, or a gamepad. There are no touch '
      + 'controls yet, so on a phone or tablet there is no way to move him at all.'),
    line('p', BODY, 'Come back on a desktop or a laptop and he will be waiting.'),
    anyway,
    line('span', SMALL, 'only useful if you have a keyboard attached'),
  );
  wrap.append(box);
  root.append(wrap);
}
